import { useNavigate } from 'react-router-dom';
import { Pill, Segmented, Sheet, useToast } from '../components';
import { useMe } from './Session';
import { useTheme } from './ThemeProvider';
import { useRefresh } from './DataProvider';
import { NAV_ITEMS } from './Nav';
import { IconLogout } from './icons';
import { api, USING_MOCK } from '../lib/api';
import { initials } from '../lib/format';
import type { ThemePreference } from '../lib/types';
import './shell.css';

/**
 * Everything that is not a screen: the secondary sections, the theme, and the
 * two kinds of sign-out. One sheet, reachable from the header on desktop and
 * the More tab on a phone.
 */
export function AccountSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { me, can } = useMe();
  const { theme, setTheme } = useTheme();
  const { hardReset } = useRefresh();
  const navigate = useNavigate();
  const toast = useToast();

  const secondary = NAV_ITEMS.filter((i) => !i.primary && can(i.module));

  const signOut = async (scope: 'device' | 'everywhere') => {
    try {
      await api.logout(scope);
      toast({ title: scope === 'device' ? 'Signed out' : 'Signed out everywhere', tone: 'good' });
    } catch {
      toast({ title: 'Could not reach the server', message: 'Your local session was cleared anyway.', tone: 'warn' });
    }
    await hardReset();
    onClose();
  };

  return (
    <Sheet open={open} onClose={onClose} title={me?.displayName ?? 'Account'}>
      <div className="stack">
        <div className="row">
          <div className="sh-user__avatar" aria-hidden="true">{initials(me?.displayName ?? '?')}</div>
          <div style={{ minWidth: 0 }}>
            <div className="sh-user__name">{me?.email || '—'}</div>
            <div className="sh-user__role">
              {me?.isAdmin ? 'Administrator' : 'Member'} · {me?.entityCount ?? 0} entities
            </div>
          </div>
          {USING_MOCK && <Pill tone="warn" small>mock data</Pill>}
        </div>

        {secondary.length > 0 && (
          <div className="sh-menu">
            {secondary.map(({ to, label, icon: Icon }) => (
              <button
                key={to}
                type="button"
                className="sh-menu__item"
                onClick={() => { navigate(to); onClose(); }}
              >
                <Icon size={18} />
                {label}
              </button>
            ))}
          </div>
        )}

        <div className="sh-menu__sep" />

        <div className="stack-sm">
          <div className="eyebrow">Appearance</div>
          <Segmented<ThemePreference>
            ariaLabel="Theme"
            large
            value={theme}
            onChange={setTheme}
            options={[
              { value: 'system', label: 'System' },
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' },
            ]}
          />
        </div>

        <div className="sh-menu__sep" />

        <div className="sh-menu">
          <button type="button" className="sh-menu__item" onClick={() => void signOut('device')}>
            <IconLogout size={18} />
            Sign out of this device
          </button>
          <button type="button" className="sh-menu__item sh-menu__item--danger" onClick={() => void signOut('everywhere')}>
            <IconLogout size={18} />
            Sign out everywhere
          </button>
        </div>
      </div>
    </Sheet>
  );
}
